import { FC, Suspense, useState } from 'react';
import { CssBaseline, ThemeProvider, LinearProgress } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { indigo, pink } from '@mui/material/colors';
import MainNav from 'packages/main/components/Layouts/main-nav';
import * as Sentry from '@sentry/react';
import 'font-awesome/css/font-awesome.min.css';
import { useSetRecoilState } from 'recoil';
import { profileMenuOpenAtom } from 'packages/main/stores/global-recoil';
import { LoadingIndicator } from 'packages/main/components/Layouts/loading-indicator';
import { Layout, Button, Space } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import styled from 'styled-components';
import 'packages/main/components/Layouts/ant-overrides.css';
import { useTracking } from 'packages/lib/utils/hooks/use-tracking';
import { Error500Result } from 'packages/lib/common-ui/error-pages/error-page-500';
const { Sider, Content } = Layout;

const wrapperTheme = createTheme({
  palette: {
    primary: indigo,
    secondary: pink,
  },
  typography: {
    fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;`,
  },
});
const useStyles = makeStyles()(() => ({
  main: {
    display: 'grid',
  },
  actionBar: { display: 'flex', alignItems: 'center' },
  header: {
    backgroundColor: '#001529 !important',
    '& .ant-space': { height: '100%' },
  },
}));

const Header = PageHeader;

const Trigger = styled(MenuUnfoldOutlined)`
  font-size: 18px;
  cursor: pointer;
  transition: color 0.3s;
  &:hover {
    color: #1890ff;
  }
`;

const TriggerF = styled(MenuFoldOutlined)`
  font-size: 18px;
  cursor: pointer;
  transition: color 0.3s;
  &:hover {
    color: #1890ff;
  }
`;

export const appBarTitleId = 'appbar-title';
export const appBarActionsId = 'appBarActions';
export const appBarTableActionsId = 'appBarTableActions';
export const appBarStickyHeaderId = 'appBarStickyHeader';
export const appBarMenuId = 'profle-menu-popover';
export const appBarMenuAnchorId = 'profile-menu-button';

export const PrivateLayout: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  useTracking();
  const { classes } = useStyles();
  const [open, setOpen] = useState(false);
  const setMenuOpen = useSetRecoilState(profileMenuOpenAtom);

  const handleProfileMenuOpen = (e: unknown): void => {
    setMenuOpen((o) => !o);
  };

  return (
    <ThemeProvider theme={wrapperTheme}>
      <Layout style={{ height: '100vh' }}>
        <CssBaseline />
        <Sider trigger={null} collapsible collapsed={!open}>
          <MainNav />
        </Sider>
        <Layout style={{ height: '100%' }}>
          <Header
            ghost={true}
            title={
              <Space size="middle">
                {!open ? (
                  <Trigger onClick={(): void => setOpen(!open)} />
                ) : (
                  <TriggerF
                    className="trigger"
                    onClick={(): void => setOpen(!open)}
                  />
                )}
                <span id={appBarTitleId} />
              </Space>
            }
            style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}
            className={classes.header}
            extra={[
              <Space className={classes.actionBar} key="actionBar">
                <span id={appBarActionsId} />
                <span id={appBarTableActionsId} />
                <div
                  style={{
                    position: 'relative',
                  }}
                >
                  <Button
                    aria-label="account of current user"
                    data-testid="profile-menu-button"
                    type="primary"
                    icon={<UserOutlined />}
                    id={appBarMenuAnchorId}
                    aria-controls={appBarMenuId}
                    aria-haspopup="true"
                    size="large"
                    shape="circle"
                    onClick={handleProfileMenuOpen}
                  />
                  <LoadingIndicator />
                </div>
              </Space>,
              <div id={appBarStickyHeaderId} key="appBarStickyHeader" />,
            ]}
          />

          <Content
            className="site-layout-background"
            style={{ overflowY: 'auto' }}
          >
            <Sentry.ErrorBoundary
              beforeCapture={(scope): void => {
                scope.setTag('location', 'PrivateLayout root');
              }}
              fallback={<Error500Result />}
            >
              <Suspense fallback={<LinearProgress />}>{children}</Suspense>
            </Sentry.ErrorBoundary>
          </Content>
        </Layout>
      </Layout>
      <div id={appBarMenuId} data-testid="profile-menu-popover" />
    </ThemeProvider>
  );
};
export default PrivateLayout;
